import {
  Button,
  darken,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  makeStyles,
} from '@material-ui/core';
import { useStores } from 'doc-mate-store/lib/hooks';
import { LoadTableRow } from 'doc-mate-store/lib/models';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useState } from 'react';
import { colors } from '../../../themes';
import LinkIcon from './LinkIcon.svg';

type Props = {
  loading: boolean;
  legId: number;
  legInfoList?: number[];
  priority: number;
  onConfirm?: () => void;
  onCancel?: () => void;
};

const PriorityAssignConfirmation: React.FC<Props> = props => {
  const {
    loading,
    legId,
    legInfoList = [],
    priority,
    onConfirm,
    onCancel,
  } = props;
  const classes = useStyles();
  const [open, setOpen] = useState(true);

  const { rootStore } = useStores();
  const linkedLegIds = [...legInfoList];
  if (!linkedLegIds.includes(legId)) {
    linkedLegIds.push(legId);
  }

  const linkedLegs = linkedLegIds
    .map(val => rootStore.loadTableRows.get(`${val}`))
    .filter((leg): leg is LoadTableRow => !!leg);

  const isMultiple = linkedLegs.length > 1;

  const sortOrderLegTableRow = useCallback(
    (legA: LoadTableRow, legB: LoadTableRow) => {
      return legA.orderNumber - legB.orderNumber;
    },
    [],
  );

  return (
    <Dialog
      aria-labelledby="priority-confirm-dialog-title"
      open={open}
      onClose={(_event, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          setOpen(false);
          if (onCancel) onCancel();
        }
      }}
      fullWidth
      maxWidth="xs"
      className={classes.dialog}
    >
      <DialogTitle disableTypography className={classes.header}>
        {isMultiple && <img src={LinkIcon} alt="Link" />}
        <span className="linked-text"> Confirmation</span>
      </DialogTitle>
      <Divider />
      <DialogContent className={classes.main}>
        <div className="driver-text">
          Priority <span className="driver-name">{priority}</span> will be
          assigned to the following {isMultiple ? 'DM-IDs' : 'DM-ID'}:
        </div>
        {linkedLegs.sort(sortOrderLegTableRow).map(val => (
          <div key={val.dmId} className="name-container">
            DM-ID
            <span className="name-value"> {val.dmId}</span>
          </div>
        ))}
        <div className={classes.actions}>
          <Button
            disabled={loading}
            className={`${classes.actionBtn} ${classes.cancelBtn}`}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            disabled={loading}
            className={`${classes.actionBtn} ${classes.saveBtn}`}
            onClick={onConfirm}
          >
            Confirm
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const useStyles = makeStyles(theme => ({
  dialog: {
    '& .MuiDialog-paperWidthXs': {
      maxWidth: 400,
    },
  },
  header: {
    fontSize: 18,
    color: '#4f4f4f',
    fontWeight: 700,
    textAlign: 'center',
    padding: theme.spacing(1),

    '& .linked-text': {
      textTransform: 'uppercase',
    },
    '& img': {
      marginRight: theme.spacing(1),
      verticalAlign: 'middle',
    },
  },
  main: {
    padding: theme.spacing(1, 0, 0),

    '& .driver-text': {
      textAlign: 'center',
      fontSize: 16,
      padding: theme.spacing(0.5, 3),
      color: '#4f4f4f',
      fontWeight: 500,
      '& .driver-name': {
        color: '#155878',
        fontWeight: 700,
      },
    },
    '& .name-container': {
      textAlign: 'center',
      padding: theme.spacing(1, 2),
      fontSize: 16,
      color: '#4f4f4f',
      '& .name-value': {
        fontWeight: 700,
      },
    },
  },

  actions: {
    alignItems: 'center',
    borderTop: '1px solid #bdbdbd',
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
    padding: theme.spacing(2, 2),
  },
  actionBtn: {
    borderRadius: 5,
    fontSize: theme.typography.body1.fontSize,
    marginLeft: theme.spacing(2),
    padding: theme.spacing(1, 6),
  },
  cancelBtn: {
    backgroundColor: colors.danger[500],
    color: 'white',

    '&:hover': {
      backgroundColor: darken(colors.danger[500], 0.15),
    },
  },
  saveBtn: {
    backgroundColor: colors.textPrimary[400],
    color: 'white',

    '&:hover': {
      backgroundColor: darken(colors.textPrimary[400], 0.15),
    },
  },
}));

export default observer(PriorityAssignConfirmation);
